import { formatDollars, formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import { BALLOON, CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';

import { MONEY_FACTOR } from '../../../constants';
import type { GetDealTypeLabelParams } from '../../dealTypeDropdown/utils/getDealTypeLabels';

export type ScenarioPaymentSummary = {
    amount: string;
    unit: string;
    details: string[];
};

const MONTHLY_UNIT = '/mo';

const formatTerm = (months: number | string | undefined) => `${months ?? 0} mo`;

const formatRate = ({ rateTypeVal, rateTypeLabel }: GetDealTypeLabelParams) => {
    const label = rateTypeLabel.trim();
    return label === MONEY_FACTOR ? `${MONEY_FACTOR} ${rateTypeVal}` : `${rateTypeVal}% ${label}`;
};

const formatDown = (amount: number) => `${formatDollars(amount)} down`;

const getMonthlySummary = (params: GetDealTypeLabelParams, extraDetails: string[] = []): ScenarioPaymentSummary => ({
    amount: formatDollarsAndCents(params.monthlyPayment),
    unit: MONTHLY_UNIT,
    details: [formatTerm(params.months), formatRate(params), formatDown(params.downPayment), ...extraDetails]
});

const getFlexBuySummary = (params: GetDealTypeLabelParams): ScenarioPaymentSummary => {
    const { firstPayment, secondPayment, firstTerm, secondTerm } = params.flexBuyDetails;
    return {
        amount: `${formatDollarsAndCents(firstPayment)}/${formatDollarsAndCents(secondPayment)}`,
        unit: MONTHLY_UNIT,
        details: [`${firstTerm ?? ''}/${secondTerm ?? ''} mo`, formatRate(params), formatDown(params.downPayment)]
    };
};

const getOnePayLeaseSummary = (params: GetDealTypeLabelParams): ScenarioPaymentSummary => ({
    amount: formatDollarsAndCents(params.dueAtSigning),
    unit: 'one pay',
    details: [formatTerm(params.months), formatRate(params)]
});

export const getScenarioPaymentSummary = (params: GetDealTypeLabelParams): ScenarioPaymentSummary | undefined => {
    switch (params.currentOfferType) {
        case CASH:
            return { amount: formatDollarsAndCents(params.totalAmountDue), unit: 'due', details: [formatDown(params.cashDown)] };
        case LEASE:
            return params.isOnePayLease ? getOnePayLeaseSummary(params) : getMonthlySummary(params);
        case FINANCE:
            return params.isFlexBuyPayment ? getFlexBuySummary(params) : getMonthlySummary(params);
        case BALLOON:
            return getMonthlySummary(params, [`${formatDollars(params.balloonAmount)} balloon`]);
        default:
            return undefined;
    }
};
